import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { BlindKaizoRace } from '../../../../types/raceTypes';
import { SearchableSelectOption } from '../../../common/filters/SearchableSelect';

export function useFilters(races: BlindKaizoRace[]) {
	const [searchParams, setSearchParams] = useSearchParams();

	const filterCreator = searchParams.get('creator') ?? null;
	const filterGameType = searchParams.get('gameType') ?? null;
	const filterRacer = searchParams.get('racer') ?? null;

	const filterOptions = useMemo(() => {
		const creatorMap = new Map<string, string>();
		const gameMap = new Map<string, string>();
		const racerMap = new Map<string, string>();
		for (const race of races) {
			for (const c of race.creators || []) {
				const key = c
				if (key) creatorMap.set(key, c);
			}
			const game = race.game as { _id?: string; name?: string; abbreviation?: string } | string | undefined;
			if (game != null) {
				if (typeof game === 'object' && game._id) {
					gameMap.set(game._id, game.name ?? game.abbreviation ?? game._id);
				} else if (typeof game === 'string') {
					gameMap.set(game, game);
				}
			}
			for (const r of race.racers || []) {
				if (!r.user) continue;
				const key = r.user
				if (key) racerMap.set(key, r.user);
			}
		}
		const creators: SearchableSelectOption[] = Array.from(creatorMap.entries())
			.map(([value, label]) => ({ value, label }))
			.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
		const games: SearchableSelectOption[] = Array.from(gameMap.entries())
			.map(([value, label]) => ({ value, label }))
			.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
		const racers: SearchableSelectOption[] = Array.from(racerMap.entries())
			.map(([value, label]) => ({ value, label }))
			.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
		return { creators, games, racers };
	}, [races]);

	const filteredRaces = useMemo(() => {
		let list = races;
		if (filterCreator) {
			list = list.filter(race =>
				(race.creators || []).some(c => c === filterCreator)
			);
		}
		if (filterGameType) {
			list = list.filter(race => {
				const game = race.game as { _id?: string } | string | undefined;
				const gameId = game == null ? undefined : typeof game === 'object' ? game._id : game;
				return gameId === filterGameType;
			});
		}
		if (filterRacer) {
			list = list.filter(race =>
				(race.racers || []).some(r => r.user && r.user === filterRacer)
			);
		}
		return list;
	}, [races, filterCreator, filterGameType, filterRacer]);

	const setFilter = (key: 'creator' | 'gameType' | 'racer', value: string | null, onFilterChange?: () => void) => {
		const next = new URLSearchParams(searchParams);
		if (value) next.set(key, value);
		else next.delete(key);
		next.delete('page');
		setSearchParams(next, { replace: true });
		onFilterChange?.();
	};

	return {
		filterCreator,
		filterGameType,
		filterRacer,
		filterOptions,
		filteredRaces,
		setFilter,
	};
}

